//ClinetReports.js
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import ClientLayout from "../../layouts/ClientLayout";
import {
  Search, HardHat, CheckCircle2, FileText, Download, Eye,
  CalendarDays, Inbox, Layers, Send, XCircle,
} from "lucide-react";
// FIX: this file used to build its own base URL and call bare fetch() with a
// manually-attached Authorization header. That guessed URL was landing on
// something (a static server / catch-all route) that answers every path with
// the React app's own index.html and a 200 status — so every "fetch" here
// silently got back HTML instead of JSON. ProjectReports.js never has this
// problem because it always goes through the shared axios instance below,
// which already has the correct base URL and auth wired up (proven working
// for /reports, /uploads/:fileUrl, etc.). Routing every call in this file
// through the same instance fixes it the same way.
import API from "../../services/api";

/* ─── Design tokens — matched to ProjectReports.js palette ── */
/* NOTE: key names (orange/orangeSoft/orangeMid) kept as-is to avoid touching
   every reference across the file — only the underlying hex values were
   swapped to the indigo/violet palette used by ProjectReports.js so colors
   and icons feel consistent across both screens. */
const T = {
  bg:          "#F8FAFC",
  surface:     "#FFFFFF",
  orange:      "#4F46E5",   // primary accent — now indigo, same as ProjectReports.js
  orangeSoft:  "#EEF2FF",
  orangeMid:   "#6366F1",
  text:        "#0F172A",
  textSec:     "#64748B",
  border:      "#E2E8F0",
  borderMd:    "#CBD5E1",
  green:       "#059669",
  greenSoft:   "#ECFDF5",
  amber:       "#D97706",
  amberSoft:   "#FFFBEB",
  red:         "#E11D48",
  redSoft:     "#FFF1F2",
  violet:      "#7C3AED",
  violetSoft:  "#F5F3FF",
  grayIcon:    "#64748B",
  graySoft:    "#F1F5F9",
  violetFaint: "#F7F3FD",
  borderSolid: "#E8E0F5",
  shadow:      "0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.05)",
  shadowHov:   "0 4px 20px rgba(0,0,0,0.10)",
  font:        "'Montserrat','Segoe UI',sans-serif",
  radius:      "18px",
  radiusSm:    "10px",
  radiusXs:    "6px",
};

/* ─── Stage config — icon + accent per library (same as ProjectReports.js STAGES) ── */
const STAGES = [
  {
    value:       "Pre Inspection",
    icon:        Search,
    label:       "Pre Inspection",
    description: "Before work begins — site survey, baseline documentation",
    accent:      "#4F46E5",
    accentLight: "#EEF2FF",
    border:      "#C7D2FE",
  },
  {
    value:       "WIP",
    icon:        HardHat,
    label:       "WIP",
    description: "Work In Progress — mid-construction milestone reports",
    accent:      "#D97706",
    accentLight: "#FFFBEB",
    border:      "#FDE68A",
  },
  {
    value:       "Post Site Completion",
    icon:        CheckCircle2,
    label:       "Post Site Completion",
    description: "Final inspection after all site work is complete",
    accent:      "#059669",
    accentLight: "#ECFDF5",
    border:      "#A7F3D0",
  },
];

/* ─── STATUS CONFIG — same 4 states as ProjectReports.js STATUS_CONFIG ──
   FIX (status not fetched properly): the old stCfg() only lowercased the
   status and matched exact strings "approved" / "pending" / "rejected".
   Any multi-word status like "Under Review" (and anything with different
   casing/spacing) fell through to a blank "—" pill instead of showing the
   real status. It also had no default when status was missing, unlike
   ProjectReports.js which defaults missing status to "pending". stCfg now
   normalizes the same way ProjectReports.js does
   ((status || "pending").toLowerCase().replace(/\s+/g,"_")) and looks it up
   in the same 4-state config, so the pill always shows something meaningful. */
const STATUS_CONFIG = {
  pending:      { label: "Pending",      color: "#B45309", bg: "#FFFBEB", border: "#FCD34D", dot: "#F59E0B" },
  approved:     { label: "Approved",     color: "#065F46", bg: "#ECFDF5", border: "#6EE7B7", dot: "#10B981" },
  rejected:     { label: "Rejected",     color: "#9F1239", bg: "#FFF1F2", border: "#FECDD3", dot: "#F43F5E" },
  under_review: { label: "Under Review", color: "#5B21B6", bg: "#F5F3FF", border: "#DDD6FE", dot: "#7C3AED" },
};
const stCfg = (s) => {
  const key = (s || "pending").toLowerCase().replace(/\s+/g, "_");
  return STATUS_CONFIG[key] || STATUS_CONFIG.pending;
};

/* ─── Date format — same as ProjectReports.js formatDate ── */
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/* ─── Styles injected once ───────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("ar2-styles")) return;
  const s = document.createElement("style");
  s.id = "ar2-styles";
  s.textContent = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
    .ar2 *{box-sizing:border-box;font-family:'Montserrat','Segoe UI',sans-serif}

    /* KPI cards — NO left border, blob at top-right */
    .ar2-kpi{background:#fff;border-radius:14px;border:1px solid #E5E7EB;box-shadow:0 1px 2px rgba(0,0,0,0.04),0 4px 12px rgba(0,0,0,0.05);padding:20px 22px;display:flex;align-items:center;gap:16px;position:relative;overflow:hidden;transition:box-shadow .18s}
    .ar2-kpi:hover{box-shadow:0 4px 20px rgba(0,0,0,0.10)}
    .ar2-kpi-circle{position:absolute;right:-18px;top:-18px;width:80px;height:80px;border-radius:50%;opacity:.10}
    .ar2-kpi-icon{width:46px;height:46px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;flex-shrink:0}
    .ar2-kpi-label{font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#6B7280;margin-bottom:3px}
    .ar2-kpi-value{font-size:26px;font-weight:700;color:#111827;line-height:1}

    /* Stage / library cards — matched to ProjectReports.js "Quick Upload by Stage" cards */
    .ar2-lib{transition:transform .18s ease, box-shadow .18s ease;}
    .ar2-lib:hover{transform:translateY(-4px);}
    .ar2-lib-arrow{transition:background .18s, color .18s;}

    /* Type cards */
    .ar2-type{background:#fff;border-radius:${T.radiusSm};border:1.5px solid ${T.border};cursor:pointer;transition:all .2s;padding:15px 18px;display:flex;align-items:center;justify-content:space-between;gap:12px}
    .ar2-type:hover{border-color:${T.orange};box-shadow:0 2px 12px rgba(79,70,229,.14);transform:translateY(-2px)}
    .ar2-type:hover .ar2-type-arrow{transform:translateX(4px);color:${T.orange}}
    .ar2-type-arrow{transition:all .18s;color:${T.textSec}}

    /* Table — matched to ProjectReports.js table */
    .ar2-row{transition:background .12s ease;}
    .ar2-row:hover{background:${T.violetFaint} !important;}
    .ar2-action-btn{transition:background .13s, color .13s, transform .11s;}
    .ar2-action-btn:hover{transform:scale(1.06);}

    /* Buttons */
    .ar2-btn-back{display:inline-flex;align-items:center;gap:7px;padding:8px 16px;border-radius:${T.radiusSm};font-size:13px;font-weight:600;cursor:pointer;background:#fff;color:${T.text};border:1.5px solid ${T.border};transition:all .15s}
    .ar2-btn-back:hover{border-color:${T.orange};color:${T.orange}}

    /* Pagination */
    .ar2-pg{width:34px;height:34px;display:flex;align-items:center;justify-content:center;border-radius:${T.radiusXs};font-size:13px;font-weight:700;cursor:pointer;border:1.5px solid ${T.borderSolid};background:#fff;color:${T.textSec};transition:all .15s}
    .ar2-pg:hover{border-color:${T.orange};color:${T.orange}}
    .ar2-pg.on{background:linear-gradient(135deg,${T.orange},${T.orangeMid});color:#fff;border-color:${T.orange};box-shadow:0 2px 8px rgba(79,70,229,.35)}

    /* Status pill */
    .ar2-pill{display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:20px;font-size:11.5px;font-weight:700}

    /* Responsive */
    @media(max-width:1024px){.ar2-kpi-grid{grid-template-columns:repeat(2,1fr)!important}.ar2-lib-grid{grid-template-columns:repeat(2,1fr)!important}}
    @media(max-width:640px){.ar2-kpi-grid{grid-template-columns:1fr 1fr!important}.ar2-lib-grid{grid-template-columns:1fr!important}.ar2-head-row{flex-direction:column;align-items:flex-start!important;gap:12px!important}.ar2-table-scroll{font-size:12px!important}}
  `;
  document.head.appendChild(s);
};

/* ─── Inline SVG set (kept for breadcrumb/back/arrow/empty — unrelated to status/table) ── */
const Ico = {
  back:  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>,
  arrow: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
  doc:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  docLg: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  empty: <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
};

/* ══════════════════════════════════════════════════════════
   MAIN — ALL API / STATE / PAGINATION LOGIC UNTOUCHED
   (now routed through the shared axios API instance — see import comment above)
══════════════════════════════════════════════════════════ */


export default function ApprovedReports() {
  const navigate = useNavigate();
  const projectId = localStorage.getItem("project_id");
console.log("Project ID =", projectId);
  const role = localStorage.getItem("role")?.toLowerCase();

  const [loadError, setLoadError] = useState("");

  // Shared axios-error → readable-message helper, same shape ProjectReports.js
  // uses in its catch blocks (err.response?.data?.message).
  const describeError = (err, fallback) =>
    err.response?.data?.message ||
    (err.response ? `Request failed (${err.response.status}).` : err.message) ||
    fallback;

  /* ── API functions — now use the shared axios `API` instance instead of
     bare fetch() + a hand-built base URL (see import comment above). ── */
  // FIX: /reports/libraries/:projectId turned out to be an unreliable
  // endpoint — it returned data for some accounts and nothing for others,
  // even though the underlying /reports/types and /reports/list endpoints
  // (used by "Download All" and every drill-down below) worked fine for
  // everyone. That's because the three categories were never actually
  // dynamic server data — they're the same fixed STAGES list ProjectReports.js
  // uses everywhere (Quick Upload by Stage, the stage filter tabs, etc). So
  // Page 1 now renders STAGES directly instead of "libraries" fetched from
  // that endpoint, and this just totals up the real report count across all
  // three stages for the header badge.
  const [totalReportsCount, setTotalReportsCount] = useState(0);

  const computeTotalReportsCount = async () => {
    if (!projectId) return;
    try {
      const counts = await Promise.all(
        STAGES.map((s) =>
          fetchAllReportsForLibrary(s.value)
            .then(({ reports }) => reports.length)
            .catch((err) => {
              console.error(`Count fetch failed for "${s.value}":`, err);
              return 0;
            })
        )
      );
      setTotalReportsCount(counts.reduce((a, b) => a + b, 0));
    } catch (err) {
      console.error("Total reports count error:", err);
    }
  };

  const loadReportTypes = async (libraryName) => {
    setLoadError("");
    try {
      setSelectedLibrary(libraryName);
      const res = await API.get(`/reports/types/${projectId}/${encodeURIComponent(libraryName)}`);
      console.log("Types =", res.data);
      setReportTypes(res.data.types || []);
      setPage(2);
    } catch (err) {
      console.error("Report types fetch error:", err);
      setReportTypes([]);
      setLoadError(describeError(err, "Failed to load report categories."));
    }
  };

 const loadReports = async (library, reportType) => {
    setLoadError("");
    try {
      const res = await API.get(
        `/reports/list/${projectId}/${encodeURIComponent(library)}/${encodeURIComponent(reportType)}`
      );
      console.log("Reports =", res.data);
      setReports(res.data.reports || []);
      setPage(3);
    } catch (err) {
      console.error("Reports fetch error:", err);
      setReports([]);
      setLoadError(describeError(err, "Failed to load reports."));
    }
  };

  // FIX (ported from ProjectReports.js handleView/handleDownload/fetchFileBlob):
  // both "View" and "Download" now go through the same authenticated axios
  // call ProjectReports.js already proved works — API.get(`/uploads/:fileUrl`,
  // { responseType: "blob" }) — instead of a hand-built URL + bare fetch. The
  // resulting blob is opened via a temporary <a target="_blank"> (view) or
  // <a download> (download), same trick ProjectReports.js uses to work around
  // Chrome blocking blob: navigation in a pre-opened tab.
  const fetchFileBlob = async (fileUrl) => {
    if (fileUrl.startsWith("http")) {
      const res = await fetch(fileUrl);
      if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
      return res.blob();
    }
    const res = await API.get(`/uploads/${fileUrl}`, { responseType: "blob" });
    return res.data;
  };

  const [viewingId, setViewingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleViewReport = async (r) => {
    if (!r.file_url) return;
    setViewingId(r.id);
    try {
      const blob = await fetchFileBlob(r.file_url);
      const blobUrl = window.URL.createObjectURL(blob);

      // Same technique as ProjectReports.js handleView — Chrome blocks
      // navigating a pre-opened window.open() tab to a blob: URL created
      // afterwards, so we click a target="_blank" anchor instead.
      const link = document.createElement("a");
      link.href = blobUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error("View error:", err);
      alert(describeError(err, "Failed to open attachment."));
    } finally {
      setViewingId(null);
    }
  };

  const handleDownloadReport = async (r) => {
    if (!r.file_url) return;
    setDownloadingId(r.id);
    try {
      const blob = await fetchFileBlob(r.file_url);
      const blobUrl = window.URL.createObjectURL(blob);

      const urlParts = r.file_url.split(".");
      const ext = urlParts.length > 1 ? urlParts.pop() : "";
      const safeName = (r.title || "report").replace(/[^a-z0-9_\-]+/gi, "_");
      const fileName = ext ? `${safeName}.${ext}` : safeName;

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download error:", err);
      alert(describeError(err, "Failed to download file."));
    } finally {
      setDownloadingId(null);
    }
  };

  // Shared helper — pulls every report under a library (across all its
  // report types) using the axios API instance, same endpoints as the
  // paged loaders above.
  const fetchAllReportsForLibrary = async (libraryName) => {
    const typesRes = await API.get(`/reports/types/${projectId}/${encodeURIComponent(libraryName)}`);
    const types = typesRes.data.types || [];
    if (types.length === 0) return { types, reports: [] };

    let allReports = [];
    for (const t of types) {
      const res = await API.get(
        `/reports/list/${projectId}/${encodeURIComponent(libraryName)}/${encodeURIComponent(t.report_type)}`
      );
      allReports = allReports.concat(res.data.reports || []);
    }
    return { types, reports: allReports };
  };

  // NEW — download every report under a selected library (across all its report types)
  const handleBulkDownload = async () => {
    if (!bulkLibrary) {
      alert("Please select a category first.");
      return;
    }
    setBulkDownloading(true);
    try {
      const { types, reports: allReports } = await fetchAllReportsForLibrary(bulkLibrary);

      if (types.length === 0) {
        alert(`No report types found for "${bulkLibrary}".`);
        return;
      }

      const downloadable = allReports.filter((r) => r.file_url);
      if (downloadable.length === 0) {
        alert(`No downloadable files found under "${bulkLibrary}".`);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const r of downloadable) {
        try {
          const blob = await fetchFileBlob(r.file_url);
          const blobUrl = window.URL.createObjectURL(blob);

          const urlParts = r.file_url.split(".");
          const ext = urlParts.length > 1 ? urlParts.pop() : "";
          const safeName = (r.title || "report").replace(/[^a-z0-9_\-]+/gi, "_");
          const fileName = ext ? `${safeName}.${ext}` : safeName;

          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);

          successCount++;
          await new Promise((resolve) => setTimeout(resolve, 400)); // avoid browser throttling
        } catch (err) {
          console.error("Download failed for report:", r.id, err);
          failCount++;
        }
      }

      alert(`Downloaded ${successCount} file(s)${failCount ? `, ${failCount} failed` : ""} from "${bulkLibrary}".`);
    } catch (err) {
      console.error("Bulk library download error:", err);
      alert(describeError(err, "Something went wrong while downloading."));
    } finally {
      setBulkDownloading(false);
    }
  };

  // NEW — download every report under a library (across all its report types)
  const handleDownloadAllForLibrary = async (libraryName) => {
    setDownloadingLibrary(libraryName);
    try {
      const { types, reports: allReports } = await fetchAllReportsForLibrary(libraryName);

      if (types.length === 0) {
        alert(`No report types found for "${libraryName}".`);
        return;
      }

      const downloadable = allReports.filter((r) => r.file_url);
      if (downloadable.length === 0) {
        alert(`No downloadable files found under "${libraryName}".`);
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const r of downloadable) {
        try {
          const blob = await fetchFileBlob(r.file_url);
          const blobUrl = window.URL.createObjectURL(blob);

          const urlParts = r.file_url.split(".");
          const ext = urlParts.length > 1 ? urlParts.pop() : "";
          const safeName = (r.title || "report").replace(/[^a-z0-9_\-]+/gi, "_");
          const fileName = ext ? `${safeName}.${ext}` : safeName;

          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);

          successCount++;
          await new Promise((resolve) => setTimeout(resolve, 400)); // avoid browser throttling
        } catch (err) {
          console.error("Download failed for report:", r.id, err);
          failCount++;
        }
      }

      alert(`Downloaded ${successCount} file(s)${failCount ? `, ${failCount} failed` : ""} from "${libraryName}".`);
    } catch (err) {
      console.error("Bulk library download error:", err);
      alert(describeError(err, "Something went wrong while downloading."));
    } finally {
      setDownloadingLibrary(null);
    }
  };

  /* ── State — ZERO CHANGES (only added hovStage for the restyled cards) ── */
  const [currentPage,      setCurrentPage]    = useState(1);
  const [activePage,       setActivePage]     = useState("Reports");
  const [reports,          setReports]        = useState([]);
  const [projects,         setProjects]       = useState([]);
  const [selectedLibrary,  setSelectedLibrary]= useState(null);
  const [reportTypes,      setReportTypes]    = useState([]);
  const [selectedType,     setSelectedType]   = useState("");
  const [page,             setPage]           = useState(1);
  const reportsPerPage = 5;
   // NEW — bulk download state
  // NEW — bulk download state
  const [bulkLibrary, setBulkLibrary] = useState("");
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [downloadingLibrary, setDownloadingLibrary] = useState(null);
  // NEW — purely visual hover state for the restyled stage cards (Page 1)
  const [hovStage, setHovStage] = useState(null);
  /* ── Pagination — ZERO CHANGES ── */
  const indexOfLastReport  = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports     = reports.slice(indexOfFirstReport, indexOfLastReport);
  const totalPages         = Math.ceil(reports.length / reportsPerPage);

  useEffect(() => { injectStyles(); computeTotalReportsCount(); }, []);

  /* ─────────────────────────────────────────────────────── */
  return (
    <ClientLayout>
      <div className="ar2" style={{ background: T.bg, minHeight: "100vh", padding: "clamp(16px,3vw,32px)" }}>

        {/* ── HEADER ──────────────────────────────────── */}
        <div className="ar2-head-row" style={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:28,gap:14 }}>
          <div>
            {/* Breadcrumb */}
            <div style={{ display:"flex",alignItems:"center",gap:6,fontSize:12.5,color:T.textSec,marginBottom:8 }}>
              <span style={{ cursor:"pointer",color:T.textSec }} onClick={() => navigate(-1)}>Dashboard</span>
              <span style={{ color:T.border,fontSize:14 }}>/</span>
              <span style={{ color:T.orange,fontWeight:600 }}>Reports</span>
            </div>
            <h1 style={{ margin:0,fontSize:"clamp(22px,3vw,28px)",fontWeight:800,color:T.text,lineHeight:1.15,letterSpacing:"-0.5px" }}>
              Project Reports
            </h1>
            <p style={{ margin:"5px 0 0",fontSize:13.5,color:T.textSec }}>
              View and download project documentation.
            </p>
          </div>

          {/* Total reports summary badge */}
          <div style={{ display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>

            {/* Total reports summary badge — UNCHANGED */}
            <div style={{ display:"flex",alignItems:"center",gap:10,padding:"12px 20px",background:T.surface,borderRadius:16,border:`1px solid ${T.borderSolid}`,boxShadow:T.shadow,flexShrink:0 }}>
              <div style={{ width:36,height:36,borderRadius:9,background:T.orangeSoft,display:"flex",alignItems:"center",justifyContent:"center",color:T.orange }}>
                <FileText size={18} />
              </div>
              <div>
                <div style={{ fontSize:10.5,fontWeight:700,textTransform:"uppercase",letterSpacing:".7px",color:T.textSec }}>Total Reports</div>
                <div style={{ fontSize:22,fontWeight:800,color:T.text,lineHeight:1 }}>{page === 3 ? reports.length : totalReportsCount}</div>
              </div>
            </div>

            {/* NEW — Bulk download control */}
            <div style={{ display:"flex",alignItems:"center",gap:8,padding:"12px 16px",background:T.surface,borderRadius:16,border:`1px solid ${T.borderSolid}`,boxShadow:T.shadow,flexShrink:0 }}>
              <select
                value={bulkLibrary}
                onChange={(e) => setBulkLibrary(e.target.value)}
                style={{
                  fontSize:13, fontWeight:600, color:T.text,
                  border:`1.5px solid ${T.borderSolid}`, borderRadius:9,
                  padding:"9px 12px", background:T.bg, outline:"none", cursor:"pointer",
                  minWidth:170,
                }}
              >
                <option value="">— Select category —</option>
                {STAGES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              <button
                onClick={handleBulkDownload}
                disabled={bulkDownloading || !bulkLibrary}
                style={{
                  display:"flex", alignItems:"center", gap:6,
                  fontSize:13, fontWeight:700,
                  color: (!bulkLibrary || bulkDownloading) ? T.textSec : "#fff",
                  background: (!bulkLibrary || bulkDownloading) ? T.graySoft : `linear-gradient(135deg,${T.orange},${T.orangeMid})`,
                  border:"none", borderRadius:9,
                  padding:"9px 16px",
                  cursor: (!bulkLibrary || bulkDownloading) ? "not-allowed" : "pointer",
                  whiteSpace:"nowrap",
                  boxShadow: (!bulkLibrary || bulkDownloading) ? "none" : `0 4px 14px ${T.orange}40`,
                }}
              >
                <Download size={14} />
                {bulkDownloading ? "Downloading…" : "Download All"}
              </button>
            </div>

          </div>
        </div>



        {/* Diagnostic banner — shows the real reason a load failed instead of a silent empty state */}
        {loadError && (
          <div style={{
            marginBottom:18, padding:"14px 18px", borderRadius:12,
            background:T.redSoft, border:`1px solid ${T.red}`, color:"#7F1D1D",
            fontSize:13, display:"flex", alignItems:"flex-start", gap:10,
          }}>
            <strong style={{ flexShrink:0 }}>Couldn't load data:</strong>
            <span style={{ wordBreak:"break-word" }}>{loadError}</span>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            PAGE 1 — STAGE / LIBRARY CARDS
            (restyled to match ProjectReports.js "Quick Upload by Stage" cards)
        ══════════════════════════════════════════════ */}
        {page === 1 && (
          <div style={{ background:T.surface, borderRadius:18, border:`1.5px solid ${T.border}`, overflow:"hidden", boxShadow:`0 6px 28px ${T.orange}12` }}>
            <div style={{ height:4, background:"linear-gradient(90deg,#4F46E5,#D97706,#059669)" }} />
            <div style={{ padding:"28px 30px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:26 }}>
                <div style={{ width:40, height:40, borderRadius:11, background:T.orangeSoft, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <Layers size={19} color={T.orange} />
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:16, color:T.text }}>Report Library</div>
                  <div style={{ fontSize:12, color:T.textSec, marginTop:3 }}>
                    Select a stage to view and download the reports uploaded for it
                  </div>
                </div>
              </div>

              <div className="ar2-lib-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:18 }}>
                {STAGES.map((stage) => {
                  const isHov = hovStage === stage.value;
                  return (
                    <div
                      key={stage.value}
                      className="ar2-lib"
                      onClick={() => loadReportTypes(stage.value)}
                      onMouseEnter={() => setHovStage(stage.value)}
                      onMouseLeave={() => setHovStage(null)}
                      style={{
                        border: `2px solid ${isHov ? stage.accent : stage.border}`,
                        borderRadius: 16, padding: "24px 22px", cursor: "pointer",
                        background: isHov ? stage.accentLight : T.surface,
                        boxShadow: isHov ? `0 10px 28px ${stage.accent}22` : "0 1px 4px rgba(0,0,0,0.04)",
                        display: "flex", flexDirection: "column", gap: 14,
                      }}
                    >
                      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                        <div style={{ width:50, height:50, borderRadius:14, background: isHov ? `${stage.accent}22` : stage.accentLight, border:`1.5px solid ${stage.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <stage.icon size={24} color={stage.accent} />
                        </div>
                        <div>
                          <div style={{ fontWeight:700, fontSize:15, color:T.text }}>{stage.label}</div>
                          <span style={{ display:"inline-block", marginTop:5, fontSize:9, fontWeight:800, color:stage.accent, background:stage.accentLight, border:`1px solid ${stage.border}`, padding:"2px 8px", borderRadius:6, textTransform:"uppercase", letterSpacing:"0.06em" }}>
                            {stage.value}
                          </span>
                        </div>
                      </div>

                      <p style={{ margin:0, fontSize:12, color:T.textSec, lineHeight:1.65 }}>{stage.description}</p>

                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", paddingTop:14, borderTop:`1px solid ${isHov ? `${stage.accent}30` : stage.border}` }}>
                        <span style={{ fontSize:12, fontWeight:700, color:stage.accent, display:"flex", alignItems:"center", gap:6 }}>
                          <Send size={12} /> View reports
                        </span>
                        <div className="ar2-lib-arrow" style={{ width:30, height:30, borderRadius:9, background: isHov ? stage.accent : `${stage.accent}18`, color: isHov ? "#fff" : stage.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:700 }}>→</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            PAGE 2 — REPORT TYPES
        ══════════════════════════════════════════════ */}
        {page === 2 && (
          <div style={{ background:T.surface,borderRadius:T.radius,border:`1px solid ${T.border}`,boxShadow:T.shadow,overflow:"hidden" }}>

            {/* Header bar */}
            <div style={{ padding:"20px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>
              <button className="ar2-btn-back" onClick={() => { setPage(1); setReportTypes([]); }}>
                {Ico.back} Back to Libraries
              </button>
              <div style={{ height:18,width:1,background:T.border }}/>
              <div style={{ fontSize:12.5,color:T.textSec,display:"flex",alignItems:"center",gap:6 }}>
                <span style={{ cursor:"pointer",color:T.orange,fontWeight:600 }}>Libraries</span>
                <span style={{ color:T.border }}>/</span>
                <span style={{ color:T.text,fontWeight:600 }}>{selectedLibrary}</span>
              </div>
            </div>

            {/* Section title */}
            <div style={{ padding:"20px 24px 0" }}>
              <div style={{ fontSize:10.5,fontWeight:700,textTransform:"uppercase",letterSpacing:".8px",color:T.orange,marginBottom:5 }}>
                Report Categories
              </div>
              <h2 style={{ margin:"0 0 4px",fontSize:19,fontWeight:700,color:T.text }}>{selectedLibrary}</h2>
              <p style={{ margin:"0 0 20px",fontSize:13,color:T.textSec }}>Select a category to view available documents.</p>
            </div>

            {/* Type cards */}
            <div style={{ padding:"0 24px 24px",display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(210px,1fr))",gap:12 }}>
              {reportTypes.map((item, index) => (
                <div
                  key={index}
                  className="ar2-type"
                  onClick={() => { setSelectedType(item.report_type); loadReports(selectedLibrary, item.report_type); }}
                >
                  <div style={{ display:"flex",alignItems:"center",gap:12 }}>
                    <div style={{ width:38,height:38,borderRadius:10,background:T.orangeSoft,display:"flex",alignItems:"center",justifyContent:"center",color:T.orange,flexShrink:0,border:`1px solid ${T.border}` }}>
                      {Ico.docLg}
                    </div>
                    <div>
                      <div style={{ fontSize:13.5,fontWeight:600,color:T.text,lineHeight:1.3 }}>{item.report_type}</div>
                      <div style={{ fontSize:11.5,color:T.textSec,marginTop:2 }}>View documents</div>
                    </div>
                  </div>
                  <span className="ar2-type-arrow">{Ico.arrow}</span>
                </div>
              ))}
              {reportTypes.length === 0 && (
                <div style={{ gridColumn:"1/-1",padding:"40px",textAlign:"center",color:T.textSec,fontSize:14 }}>
                  No report types found for this library.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            PAGE 3 — REPORT TABLE
            (restyled as a real <table> to match ProjectReports.js)
        ══════════════════════════════════════════════ */}
        {page === 3 && (
          <div style={{ background:T.surface, borderRadius:20, border:`1px solid ${T.borderSolid}`, overflow:"hidden", boxShadow:"0 4px 24px rgba(79,70,229,0.08)" }}>
            <div style={{ height:4, background:`linear-gradient(90deg,${T.orange},${T.violet},${T.red},${T.amber})` }} />

            {/* Table toolbar */}
            <div style={{ padding:"18px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12 }}>
              <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                <button className="ar2-btn-back" onClick={() => { setPage(2); setReports([]); }}>
                  {Ico.back} Back
                </button>
                <div style={{ height:18,width:1,background:T.border }}/>
                <nav style={{ fontSize:12.5,color:T.textSec,display:"flex",alignItems:"center",gap:6 }}>
                  <span style={{ cursor:"pointer",color:T.orange,fontWeight:600 }} onClick={() => { setPage(1); setReportTypes([]); }}>Libraries</span>
                  <span style={{ color:T.border }}>/</span>
                  <span style={{ cursor:"pointer",color:T.orange,fontWeight:600 }} onClick={() => { setPage(2); setReports([]); }}>{selectedLibrary}</span>
                  <span style={{ color:T.border }}>/</span>
                  <span style={{ color:T.text,fontWeight:600 }}>{selectedType}</span>
                </nav>
              </div>
              <span style={{ fontSize:12,color:T.textSec,background:T.bg,padding:"5px 14px",borderRadius:7,border:`1px solid ${T.border}` }}>
                {reports.length} document{reports.length !== 1 ? "s" : ""}
              </span>
            </div>

            {/* Empty state */}
            {currentReports.length === 0 ? (
              <div style={{ padding:"64px 40px", textAlign:"center" }}>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:14, maxWidth:340, margin:"0 auto" }}>
                  <div style={{ width:68, height:68, borderRadius:"50%", background:`linear-gradient(135deg,${T.orangeSoft},${T.violetSoft})`, display:"flex", alignItems:"center", justifyContent:"center", border:`1.5px solid ${T.border}` }}>
                    <Inbox size={30} color={T.orange} />
                  </div>
                  <div style={{ fontWeight:700, fontSize:16, color:T.text }}>No Reports Available</div>
                  <div style={{ fontSize:13, color:T.textSec, lineHeight:1.65, textAlign:"center" }}>
                    Reports for this category will appear here once uploaded.
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="ar2-table-scroll" style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"separate", borderSpacing:0, minWidth:820 }}>
                    <thead>
                      <tr style={{ background:T.violetFaint }}>
                        {["#","Document","Description","Status","Upload Date","Actions"].map((h, i) => (
                          <th key={h} style={{
                            fontSize:10, fontWeight:700, color:T.textSec,
                            textTransform:"uppercase", letterSpacing:"0.09em",
                            padding:"13px 16px", textAlign: i === 0 || i === 5 ? "center" : "left",
                            borderBottom:`2px solid ${T.borderSolid}`,
                            borderTop:`1px solid ${T.borderSolid}`,
                            whiteSpace:"nowrap", background:T.violetFaint,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentReports.map((r, i) => {
                        const st = stCfg(r.status);
                        const rowNum = indexOfFirstReport + i + 1;
                        const isLast = i === currentReports.length - 1;
                        return (
                          <tr key={r.id} className="ar2-row" style={{ borderBottom: isLast ? "none" : `1px solid ${T.borderSolid}` }}>

                            {/* # */}
                            <td style={{ padding:"15px 16px", textAlign:"center" }}>
                              <span style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:26, height:26, borderRadius:8, background:T.violetFaint, fontWeight:700, fontSize:11, color:T.textSec }}>{rowNum}</span>
                            </td>

                            {/* Document name */}
                            <td style={{ padding:"15px 16px", maxWidth:220 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                                <div style={{ width:32, height:32, borderRadius:9, background:T.orangeSoft, display:"flex", alignItems:"center", justifyContent:"center", color:T.orange, flexShrink:0, border:`1px solid ${T.border}` }}>
                                  {Ico.doc}
                                </div>
                                <span style={{ fontSize:13, fontWeight:600, color:T.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{r.title}</span>
                              </div>
                            </td>

                            {/* Description */}
                            <td style={{ padding:"15px 16px", maxWidth:240 }}>
                              <div style={{ fontSize:12, color:T.textSec, lineHeight:1.5 }}>
                                {r.details
                                  ? (r.details.length > 90 ? r.details.slice(0, 90) + "…" : r.details)
                                  : <span style={{ color:"#CBD5E1" }}>—</span>}
                              </div>
                            </td>

                            {/* Status — now uses the same 4-state normalized STATUS_CONFIG as ProjectReports.js */}
                            <td style={{ padding:"15px 16px", whiteSpace:"nowrap" }}>
                              <span className="ar2-pill" style={{ color:st.color, background:st.bg, border:`1.5px solid ${st.border}` }}>
                                <span style={{ width:7, height:7, borderRadius:"50%", background:st.dot, flexShrink:0 }} />
                                {st.label}
                              </span>
                            </td>

                            {/* Date — formatted the same way as ProjectReports.js (en-IN, dd MMM yyyy) */}
                            <td style={{ padding:"15px 16px", whiteSpace:"nowrap" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:12, color:T.textSec }}>
                                <CalendarDays size={13} />{formatDate(r.created_at)}
                              </div>
                            </td>

                            {/* Actions */}
                            <td style={{ padding:"15px 16px" }}>
                              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                                <button
                                  className="ar2-action-btn"
                                  disabled={!r.file_url || viewingId === r.id}
                                  onClick={() => handleViewReport(r)}
                                  title="View attachment"
                                  style={{
                                    display:"inline-flex", alignItems:"center", gap:5, fontSize:11, fontWeight:700,
                                    color: r.file_url ? T.orange : "#CBD5E1",
                                    background: r.file_url ? T.orangeSoft : T.graySoft,
                                    border:`1px solid ${r.file_url ? "#C7D2FE" : T.border}`,
                                    padding:"6px 12px", borderRadius:9,
                                    cursor: (!r.file_url || viewingId === r.id) ? "not-allowed" : "pointer",
                                    whiteSpace:"nowrap",
                                  }}
                                >
                                  <Eye size={12} /> {viewingId === r.id ? "Opening…" : "View"}
                                </button>
                                <button
                                  className="ar2-action-btn"
                                  disabled={!r.file_url || downloadingId === r.id}
                                  onClick={() => handleDownloadReport(r)}
                                  title="Download file"
                                  style={{
                                    display:"inline-flex", alignItems:"center", gap:5, fontSize:11, fontWeight:700,
                                    color:"#fff",
                                    background: r.file_url ? `linear-gradient(135deg,${T.orange},${T.orangeMid})` : T.graySoft,
                                    border:"none",
                                    padding:"6px 12px", borderRadius:9,
                                    cursor: (!r.file_url || downloadingId === r.id) ? "not-allowed" : "pointer",
                                    whiteSpace:"nowrap",
                                    opacity: !r.file_url ? 0.6 : 1,
                                  }}
                                >
                                  <Download size={12} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination — UNCHANGED logic, restyled to match ProjectReports.js */}
                <div style={{ padding:"14px 24px", borderTop:`1px solid ${T.borderSolid}`, display:"flex", alignItems:"center", justifyContent:"space-between", background:"#FAFBFF", flexWrap:"wrap", gap:12 }}>
                  <p style={{ margin:0, fontSize:12, color:T.textSec, fontWeight:500 }}>
                    Showing <strong style={{ color:T.text }}>{indexOfFirstReport + 1}–{Math.min(indexOfLastReport, reports.length)}</strong> of <strong style={{ color:T.text }}>{reports.length}</strong> reports
                  </p>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <button className="ar2-pg" style={{ width:"auto", padding:"0 14px" }} onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}>‹ Prev</button>
                    {[...Array(totalPages)].map((_, idx) => {
                      const p = idx + 1;
                      return (
                        <button key={p} className={`ar2-pg${currentPage === p ? " on" : ""}`}
                          onClick={() => setCurrentPage(p)}>{p}</button>
                      );
                    })}
                    <button className="ar2-pg" style={{ width:"auto", padding:"0 14px" }} onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}>Next ›</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <p style={{ textAlign:"center",marginTop:28,fontSize:11.5,color:"#D1D5DB",letterSpacing:".3px" }}>
          Asian Paints Client Portal &nbsp;·&nbsp; Report Management System &nbsp;·&nbsp; Confidential
        </p>

      </div>
    </ClientLayout>
  );
}